import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PlanLimitModal.css';

/**
 * PlanLimitModal - Global modal that displays when plan limits are exceeded
 *
 * Listens for 'plan-limit-exceeded' events dispatched by api.js interceptor
 * Provides clear upgrade path with specific limit information
 */
const PlanLimitModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [limitData, setLimitData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleLimitExceeded = (event) => {
      setLimitData(event.detail);
      setIsOpen(true);
    };

    window.addEventListener('plan-limit-exceeded', handleLimitExceeded);

    return () => {
      window.removeEventListener('plan-limit-exceeded', handleLimitExceeded);
    };
  }, []);

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate(limitData?.upgradeUrl || '/subscription');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen || !limitData) return null;

  const resourceLabel = {
    products: 'Products',
    ordersPerMonth: 'Orders This Month',
    warehouses: 'Warehouses',
    users: 'Team Members',
  }[limitData.resource] || limitData.resource;

  return (
    <div className="plan-limit-modal-overlay" onClick={handleClose}>
      <div className="plan-limit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="plan-limit-modal-header">
          <div className="plan-limit-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2>Plan Limit Reached</h2>
          <p className="plan-limit-subtitle">
            You&apos;ve reached your plan&apos;s limit for {resourceLabel}
          </p>
        </div>

        <div className="plan-limit-modal-body">
          <div className="plan-limit-stats">
            <div className="plan-limit-stat">
              <span className="stat-label">Current Usage</span>
              <span className="stat-value">{limitData.current}</span>
            </div>
            <div className="plan-limit-divider">/</div>
            <div className="plan-limit-stat">
              <span className="stat-label">Plan Limit</span>
              <span className="stat-value">{limitData.limit}</span>
            </div>
          </div>

          <div className="plan-limit-message">
            <p>{limitData.message}</p>
          </div>

          <div className="plan-limit-benefits">
            <h3>Upgrade to unlock:</h3>
            <ul>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Higher limits for all resources</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Advanced analytics and reporting</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Priority support and onboarding</span>
              </li>
              <li>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Amazon integration and automation</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="plan-limit-modal-footer">
          <button className="btn-secondary" onClick={handleClose}>
            Close
          </button>
          <button className="btn-primary" onClick={handleUpgrade}>
            View Plans & Upgrade
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanLimitModal;
